import { z } from 'zod';

export const walkthroughPolicySchema = z.object({ petThreshold: z.number().int().min(1).max(100).nullable() }).strict();
export const walkthroughAssessmentSchema = z
  .object({
    manualRequired: z.boolean(),
    petCount: z.number().int().min(0).max(100).nullable(),
  })
  .strict();
export type WalkthroughPolicy = z.infer<typeof walkthroughPolicySchema>;
export type WalkthroughAssessment = z.infer<typeof walkthroughAssessmentSchema>;

/** Pure decision only. Clearing a gate needs a scoped, current office review. */
export function walkthroughGate(policy: WalkthroughPolicy | null, assessment: WalkthroughAssessment) {
  policy = policy === null ? null : walkthroughPolicySchema.parse(policy);
  assessment = walkthroughAssessmentSchema.parse(assessment);
  const reasons: Array<'manual' | 'pet_threshold' | 'pet_count_unknown'> = [];
  if (assessment.manualRequired) reasons.push('manual');
  if (policy?.petThreshold != null) {
    if (assessment.petCount === null) reasons.push('pet_count_unknown');
    else if (assessment.petCount >= policy.petThreshold) reasons.push('pet_threshold');
  }
  return { required: reasons.length > 0, reasons, canAutomaticallyPrice: reasons.length === 0 };
}

/** Recomputed on the server. A policy edit invalidates an earlier review. */
export function walkthroughAssessmentKey(
  policy: WalkthroughPolicy | null,
  assessment: WalkthroughAssessment,
  revisions: { policy: number; assessment: number },
): string {
  const checkedAssessment = walkthroughAssessmentSchema.parse(assessment);
  const checkedPolicy = policy === null ? null : walkthroughPolicySchema.parse(policy);
  const checkedRevisions = z
    .object({
      policy: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
      assessment: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    })
    .strict()
    .parse(revisions);
  return JSON.stringify([
    checkedRevisions.policy,
    checkedRevisions.assessment,
    checkedPolicy?.petThreshold ?? null,
    checkedAssessment.manualRequired,
    checkedAssessment.petCount,
  ]);
}

export interface WalkthroughReviewContext {
  quoteId: string;
  companyId: string;
  locationId: string;
  propertyId: string | null;
  assessmentKey: string;
}

export interface WalkthroughReview extends WalkthroughReviewContext {
  reviewedByUserId: string;
  reviewedAt: string;
}

/**
 * Only a separate authorized office-review write may mint the review fields.
 * Neither an intake edit nor a booking request supplies or clears a review.
 */
export function walkthroughReviewMatches(review: WalkthroughReview | null, current: WalkthroughReviewContext): boolean {
  return (
    review !== null &&
    current.quoteId.length > 0 &&
    review.quoteId === current.quoteId &&
    review.reviewedByUserId.length > 0 &&
    Number.isFinite(Date.parse(review.reviewedAt)) &&
    review.companyId === current.companyId &&
    review.locationId === current.locationId &&
    review.propertyId === current.propertyId &&
    review.assessmentKey === current.assessmentKey
  );
}

const revision = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
const policySnapshotSchema = z.object({ revision, rules: walkthroughPolicySchema.nullable() }).strict();
export type WalkthroughPolicySnapshot = z.infer<typeof policySnapshotSchema>;
const reviewSchema = z
  .object({
    quoteId: z.string(),
    companyId: z.string(),
    locationId: z.string(),
    propertyId: z.string().nullable(),
    assessmentKey: z.string(),
    reviewedByUserId: z.string(),
    reviewedAt: z.string(),
  })
  .strict();
const stateSchema = z
  .object({
    policy: policySnapshotSchema,
    assessment: walkthroughAssessmentSchema,
    assessmentRevision: revision,
    review: reviewSchema.nullable(),
  })
  .strict();
export type QuoteWalkthrough = z.infer<typeof stateSchema>;
export const EMPTY_WALKTHROUGH_POLICY: WalkthroughPolicySnapshot = { revision: 0, rules: null };

export function readWalkthroughPolicy(json: string | null | undefined): WalkthroughPolicySnapshot {
  return json ? policySnapshotSchema.parse(JSON.parse(json)) : { ...EMPTY_WALKTHROUGH_POLICY };
}
export function nextWalkthroughPolicy(
  current: WalkthroughPolicySnapshot,
  rules: WalkthroughPolicy | null,
): WalkthroughPolicySnapshot {
  return policySnapshotSchema.parse({ revision: current.revision + 1, rules });
}
export function seedQuoteWalkthrough(policy = EMPTY_WALKTHROUGH_POLICY): QuoteWalkthrough {
  return { policy, assessment: { manualRequired: false, petCount: null }, assessmentRevision: 0, review: null };
}
export function readQuoteWalkthrough(json: string | null | undefined): QuoteWalkthrough {
  return json ? stateSchema.parse(JSON.parse(json)) : seedQuoteWalkthrough();
}
export interface WalkthroughQuoteContext {
  id: string;
  companyId: string;
  locationId: string | null;
  propertyId: string | null;
}
export function walkthroughContext(
  state: QuoteWalkthrough,
  context: WalkthroughQuoteContext,
): WalkthroughReviewContext {
  return {
    quoteId: context.id,
    companyId: context.companyId,
    locationId: context.locationId ?? '',
    propertyId: context.propertyId,
    assessmentKey: walkthroughAssessmentKey(state.policy.rules, state.assessment, {
      policy: state.policy.revision,
      assessment: state.assessmentRevision,
    }),
  };
}
export function walkthroughStatus(
  state: QuoteWalkthrough,
  context: WalkthroughQuoteContext,
  currentPolicy?: WalkthroughPolicySnapshot,
) {
  const policyChanged = currentPolicy !== undefined && currentPolicy.revision !== state.policy.revision;
  const gate = walkthroughGate(currentPolicy ? currentPolicy.rules : state.policy.rules, state.assessment);
  const reviewed = !policyChanged && walkthroughReviewMatches(state.review, walkthroughContext(state, context));
  return {
    ...gate,
    policyChanged,
    reviewed,
    pricingBlocked: policyChanged || (gate.required && !reviewed),
    assessmentKey: walkthroughContext(state, context).assessmentKey,
  };
}
export function changeQuoteWalkthrough(
  state: QuoteWalkthrough,
  policy: WalkthroughPolicySnapshot,
  assessment: WalkthroughAssessment | undefined,
  contextChanged: boolean,
): QuoteWalkthrough {
  const nextAssessment = assessment === undefined ? state.assessment : walkthroughAssessmentSchema.parse(assessment);
  const changed =
    contextChanged ||
    state.policy.revision !== policy.revision ||
    state.assessment.manualRequired !== nextAssessment.manualRequired ||
    state.assessment.petCount !== nextAssessment.petCount;
  return stateSchema.parse({
    ...state,
    policy,
    assessment: nextAssessment,
    assessmentRevision: state.assessmentRevision + (changed ? 1 : 0),
    review: changed ? null : state.review,
  });
}
